import { getPresignedUrlExpiresAtMs } from "./presignedUrl";

const URL_IN_HTML_RE = /https?:\/\/[^\s"'<>]+/g;

/** Presigned links in document order — the ones with an `Expires`. */
function presignedLinksIn(html: string): string[] {
  const out: string[] = [];
  for (const match of html.match(URL_IN_HTML_RE) ?? []) {
    if (getPresignedUrlExpiresAtMs(match) !== null) out.push(match);
  }
  return out;
}

/**
 * Old link → new link between two transforms of the same original. The
 * rewrite replaces the same references in the same order, so the two lists
 * zip; a length mismatch means the documents differ and nothing is safe to
 * pair. Empty when every link is unchanged.
 */
export function presignedLinkRenewals(
  previousHtml: string,
  nextHtml: string,
): Record<string, string> | null {
  const before = presignedLinksIn(previousHtml);
  const after = presignedLinksIn(nextHtml);
  if (before.length !== after.length) return null;
  const map: Record<string, string> = {};
  for (let i = 0; i < before.length; i++) {
    if (before[i] !== after[i]) map[before[i]] = after[i];
  }
  return map;
}

export interface HtmlIslandSplit {
  /** The document with every island emptied. */
  skeleton: string;
  /** Island innerHTML by region id. */
  islands: Map<string, string>;
}

/** Sets the editable regions aside so the rest can be compared. */
export function splitHtmlIslands(html: string): HtmlIslandSplit {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const islands = new Map<string, string>();
  for (const el of doc.querySelectorAll("[data-edit-id]")) {
    const id = el.getAttribute("data-edit-id");
    if (!id || islands.has(id)) continue;
    islands.set(id, el.innerHTML);
    el.innerHTML = "";
  }
  return { skeleton: doc.documentElement.outerHTML, islands };
}

/**
 * The regions whose text changed, when nothing else did; null when the
 * markup outside the islands differs and the document must reload.
 */
export function islandOnlyChanges(
  previous: HtmlIslandSplit,
  next: HtmlIslandSplit,
): Record<string, string> | null {
  if (previous.skeleton !== next.skeleton) return null;
  if (previous.islands.size !== next.islands.size) return null;
  const changed: Record<string, string> = {};
  for (const [id, html] of next.islands) {
    const before = previous.islands.get(id);
    if (before === undefined) return null;
    if (before !== html) changed[id] = html;
  }
  return changed;
}
